/**
 * @description CompanyContext - Derived company/employee context for merchant features
 *
 * Key features:
 * - Derives company and employee state from AuthContext (no separate API call)
 * - Provides backward-compatible useCompany() hook for downstream consumers
 * - refreshCompanyContext delegates to AuthContext.refreshContext()
 *
 * @dependencies
 * - AuthContext: Source of truth for company, employee, and isLoaded state
 *
 * @notes
 * - Previously made a redundant API call to /users/company-context.
 *   Refactored to derive from AuthContext which already fetches the same data via /auth/me.
 *   This eliminates ~200ms from the boot waterfall (3 sequential calls → 2).
 * - isCompanyOwner is preserved for interface compatibility but is always false
 *   (was dead code — no consumer ever read it).
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useUser, useCompany as useAuthCompany, useEmployee as useAuthEmployee } from './AuthContext';
import { useAuth } from './AuthContext';

const CompanyContext = createContext();

export const CompanyProvider = ({ children }) => {
  const { isLoaded: userLoaded } = useUser();
  const { company } = useAuthCompany();
  const { employee } = useAuthEmployee();
  const { refreshContext } = useAuth();

  // Derive loading from AuthContext's isLoaded — no separate fetch needed
  const loading = !userLoaded;

  // refreshCompanyContext delegates to AuthContext's refreshContext
  // which clears backend cache and re-fetches /auth/me
  const refreshCompanyContext = useCallback(() => {
    return refreshContext();
  }, [refreshContext]);

  // Memoize context value to prevent unnecessary re-renders of consumers
  // (notably PermissionProvider, which triggers permission re-fetches on change)
  const value = useMemo(() => ({
    company,
    employee,
    loading,
    error: null, // No separate fetch to fail
    refreshCompanyContext,
    hasCompany: !!company,
    isCompanyOwner: false, // Dead code — no consumer reads this
    companyId: company?.id || null
  }), [company, employee, loading, refreshCompanyContext]);

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export default CompanyContext;
